import {
  TCard,
  TCardLayoutSlot,
  CardId,
} from '@/console/features/card-layout/card-layout.types';

/**
 * Maximum number of cards allowed in a single slot
 */
export const MAX_CARDS_PER_SLOT = 2;

/**
 * Updates a card's position in the provided cards array
 * @param cards - Array of cards to update
 * @param cardIndex - Index of the card to update
 * @param newPosition - New position object to assign
 * @returns Updated cards array
 */
const updateCardPosition = (
  cards: TCard[],
  cardIndex: number,
  newPosition: Partial<TCard['position']>,
): TCard[] => {
  const newCards = [...cards];

  newCards[cardIndex] = {
    ...newCards[cardIndex],
    position: {
      ...newCards[cardIndex].position,
      ...newPosition,
    },
  };

  return newCards;
};

/**
 * Returns all cards in a specific slot
 * @param cards - Array of cards to filter
 * @param slot - The slot to filter by
 * @returns Array of cards in the specified slot
 */
export const getCardsInSlot = (
  cards: TCard[],
  slot: TCardLayoutSlot,
): TCard[] => {
  return cards.filter((card) => card.position.slot === slot);
};

/**
 * Handles the special case when the Pipelines card is dropped
 * Swaps the Pipelines card with all cards in the target slot
 * @returns Updated array of cards
 */
const handlePipelineDrop = (
  cards: TCard[],
  draggedCard: TCard,
  draggedCardIndex: number,
  targetSlot: TCardLayoutSlot,
  cardsInTargetSlot: TCard[],
): TCard[] => {
  let updatedCards = [...cards];
  const pipelineOriginalSlot = draggedCard.position.slot;

  // Move all cards from target slot to pipeline's original slot
  cardsInTargetSlot.forEach((card) => {
    const cardIndex = updatedCards.findIndex((c) => c.id === card.id);
    updatedCards = updateCardPosition(updatedCards, cardIndex, {
      slot: pipelineOriginalSlot,
    });
  });

  // Move pipeline card to target slot
  updatedCards = updateCardPosition(updatedCards, draggedCardIndex, {
    slot: targetSlot,
  });

  return updatedCards;
};

/**
 * Handles the special case when a card is dropped on the Pipelines card
 * Swaps the dropped card (and any paired card) with the Pipelines card
 * @returns Updated array of cards
 */
const handleDropOnPipeline = (
  cards: TCard[],
  draggedCard: TCard,
  draggedOverCard: TCard,
  draggedCardIndex: number,
): TCard[] => {
  let updatedCards = [...cards];
  const pipelineSlot = draggedOverCard.position.slot;
  const draggedCardSlot = draggedCard.position.slot;

  // Find if there's another card paired with the dragged card
  const otherCardInSameSlot = updatedCards.find(
    (card) =>
      card.position.slot === draggedCardSlot && card.id !== draggedCard.id,
  );

  // Move dragged card to pipeline's slot
  updatedCards = updateCardPosition(updatedCards, draggedCardIndex, {
    slot: pipelineSlot,
  });

  // Move paired card (if exists) to pipeline's slot as well
  if (otherCardInSameSlot) {
    const otherCardIndex = updatedCards.findIndex(
      (c) => c.id === otherCardInSameSlot.id,
    );
    updatedCards = updateCardPosition(updatedCards, otherCardIndex, {
      slot: pipelineSlot,
    });
  }

  // Move pipeline to dragged card's original slot
  const pipelineIndex = updatedCards.findIndex(
    (c) => c.id === draggedOverCard.id,
  );
  updatedCards = updateCardPosition(updatedCards, pipelineIndex, {
    slot: draggedCardSlot,
  });

  return updatedCards;
};

/**
 * Handles the standard case when a regular card is dragged onto another regular card
 * Swaps positions of the two cards
 * @returns Updated array of cards
 */
const handleNormalCardDrop = (
  cards: TCard[],
  draggedCardIndex: number,
  draggedOverCard: TCard | null,
  draggedOverIndex: number,
  cardsInTargetSlot: TCard[],
  targetSlot: TCardLayoutSlot,
): TCard[] => {
  let updatedCards = [...cards];

  // Early return if no target card
  if (!draggedOverCard) {
    return updatedCards;
  }

  const draggedCardOriginalSlot = cards[draggedCardIndex].position.slot;
  const cardsInOriginSlot = getCardsInSlot(cards, draggedCardOriginalSlot);

  // Special case: moving from a slot with 2 cards to a slot with < 2 cards
  if (
    cardsInTargetSlot.length < MAX_CARDS_PER_SLOT &&
    cardsInOriginSlot.length === 2
  ) {
    // Adjust order for new slot
    updatedCards = updateCardPosition(updatedCards, draggedCardIndex, {
      slot: targetSlot,
      order: updatedCards[draggedCardIndex].position.order + 1,
    });
  }

  // Swap positions between dragged card and target card
  const draggedCardPosition = { ...updatedCards[draggedCardIndex].position };
  const draggedOverCardPosition = { ...draggedOverCard.position };

  updatedCards[draggedCardIndex] = {
    ...updatedCards[draggedCardIndex],
    position: draggedOverCardPosition,
  };

  updatedCards[draggedOverIndex] = {
    ...updatedCards[draggedOverIndex],
    position: draggedCardPosition,
  };

  return updatedCards;
};

/**
 * Main function to handle all card drop scenarios
 * Determines the type of drop and delegates to the appropriate handler
 * @param draggedCard - The card being dragged
 * @param draggedOverCard - The card being dropped onto (if any)
 * @param slot - The slot where the drop occurred
 * @param prevCards - The current array of cards before the drop
 * @returns Updated array of cards after the drop operation
 */
export const handleCardDrop = (
  draggedCard: TCard,
  draggedOverCard: TCard | null,
  slot: TCardLayoutSlot,
  prevCards: TCard[],
): TCard[] => {
  const updatedCards = [...prevCards];

  // Find indices for dragged and target cards
  const draggedCardIndex = updatedCards.findIndex(
    (card) => card.id === draggedCard.id,
  );
  const draggedOverIndex = updatedCards.findIndex(
    (card) => card.id === draggedOverCard?.id,
  );

  // Get all cards currently in the target slot
  const cardsInTargetSlot = getCardsInSlot(updatedCards, slot);

  // Handle special cases for Pipelines card
  if (draggedCard.id === CardId.PIPELINES) {
    // When Pipelines card is being dragged
    return handlePipelineDrop(
      updatedCards,
      draggedCard,
      draggedCardIndex,
      slot,
      cardsInTargetSlot,
    );
  } else if (draggedOverCard?.id === CardId.PIPELINES) {
    // When dropping onto Pipelines card
    return handleDropOnPipeline(
      updatedCards,
      draggedCard,
      draggedOverCard,
      draggedCardIndex,
    );
  } else {
    // Standard card swap
    return handleNormalCardDrop(
      updatedCards,
      draggedCardIndex,
      draggedOverCard,
      draggedOverIndex,
      cardsInTargetSlot,
      slot,
    );
  }
};
